import {
  ActiveCallType,
  CallManagerContext,
  OpenCallsType,
  UseCallManagerType,
} from 'contexts/call_manager_context'
import isElectron from 'is-electron'
import React, {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react'
import {
  joinCallNewDirectChat,
  joinCallTopic,
  leaveCall as leaveCallAction,
} from 'reduxes/slices/calls/actions'
import { TwilioError } from 'twilio-video'

import ErrorBoundary from '../../../containers/error_boundary'
import { AppAudioContext } from '../../../contexts/app_audio_context'
import { DevicesContext } from '../../../contexts/devices_context'
import { useAxios } from '../../../hooks/use_axios'
import { useFullscreen } from '../../../hooks/use_fullscreen'
import {
  AppLocationContext,
  TOPIC_VIEW,
} from '../../../pages/admin/app_location_provider'
import { setFetched } from '../../../reduxes/slices/api/actions'
import { getCurrentMember } from '../../../reduxes/slices/current/selectors'
import { isLiveChatApp } from '../../../reduxes/slices/live_chat_app/selectors'
import { useSelectorDelegateStore } from '../../../reduxes/stores/root_store/hooks'
import { findRecordInApiResponse } from '../../../reduxes/util/api_response_utils'
import { ParticipantManager } from '../participant_manager'
import { useCallErrors } from '../use_call_errors'
import { CONNECTED, RECONNECTING, useRoomManager } from '../use_room_manager'
import { joinRoom } from '../util/room'
import { useCallsChannel } from '../util/use_calls_channel'
import { getConnectionOptions } from '../util/web_rtc_config'
import { ActiveCallAlert } from './active_call_alert'
import {
  CE,
  CS,
  connectionStatusReducer,
  initialConnectionStatusState,
} from './connection_status_reducer'
import { useCallNavHandler } from './use_call_nav_handler'

const blockPowerSave = () => {
  const electronApi = (window as any).api
  if (!electronApi) {
    return
  }

  electronApi.send('block-power-save')
}

const unblockPowerSave = () => {
  const electronApi = (window as any).api
  if (!electronApi) {
    return
  }

  electronApi.send('unblock-power-save')
}

const MemoizedParticipantManager = memo(ParticipantManager)

export type CallManagerProps = {
  dispatches: { [id: string]: any }
  axiosInstances: { [id: string]: any }
  children?: React.ReactNode
}

// active means the user is on the call
type CallsRootStoreToSubStoreContextType = {
  setActiveMemberId?: React.Dispatch<React.SetStateAction<number | null>>

  /* eslint-disable @typescript-eslint/no-explicit-any */
  setActiveCall?: React.Dispatch<React.SetStateAction<any>>
  setActiveTopic?: React.Dispatch<React.SetStateAction<any>>
  setActiveParticipant?: React.Dispatch<React.SetStateAction<any>>
  setOpenCalls?: React.Dispatch<React.SetStateAction<any>>
  onDisconnect?: any
  connectionStatus?: CS
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

export const CallsRootStoreToSubStoreContext =
  createContext<CallsRootStoreToSubStoreContextType>({})

export const CallManager = ({
  dispatches,
  axiosInstances,
  children,
}: CallManagerProps) => {
  const [
    { current: connectionStatus, previous: prevConnectionStatus },
    dispatchConnectionAction,
  ] = useReducer(connectionStatusReducer, initialConnectionStatusState)

  // activeCall means the person is on the call, but not necessarily connected
  // (i.e. multiple tabs; can only be connected on one tab)
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const [activeCall, setActiveCall] = useState<any>(null)
  const [activeTopic, setActiveTopic] = useState<any>(null)
  const [activeParticipant, setActiveParticipant] = useState<any>(null)
  /* eslint-enable @typescript-eslint/no-explicit-any */
  const activeTopicId = activeTopic?.id

  const [activeMemberId, setActiveMemberId] = useState<number | null>(null)
  const [openCalls, setOpenCalls] = useState<OpenCallsType>({})
  const currentMember = useSelectorDelegateStore(getCurrentMember)
  const currentMemberId = currentMember?.id
  const memberIdForDispatch = activeMemberId || currentMemberId
  const dispatch = memberIdForDispatch && dispatches[memberIdForDispatch]
  const axiosInstance = useMemo(() => {
    return memberIdForDispatch && axiosInstances[memberIdForDispatch]
  }, [memberIdForDispatch, axiosInstances])
  const axios = useAxios(axiosInstance)
  const { exitFullscreen } = useFullscreen()
  const { setAppLocation } = useContext(AppLocationContext)
  const liveChatApp = useSelectorDelegateStore(isLiveChatApp)

  const [navToVideoViewAfterJoin, setNavToVideoViewAfterJoin] =
    useState<boolean>(false)

  const { room, setRoom, roomData } = useRoomManager(
    connectionStatus,
    prevConnectionStatus,
    dispatchConnectionAction,
  )
  const [fromParticipantManager, setFromParticipantManager] = useState<any>({})
  const { appLocation, appLocationCache, goBack } =
    useContext(AppLocationContext)
  const { subscribe, unsubscribe } = useCallsChannel()
  const passBackFromParticipantManager = useCallback((obj: any) => {
    setFromParticipantManager(obj)
  }, [])

  // data from the useLocalParticipant hook which needs to be called
  // inside the participant manager so that it is a child of the CallManagerContext
  const {
    publishedAudioTrack,
    publishedVideoTrack,
    publishedScreenShareTrack,
    unpublishedAudioTrack,
    unpublishedVideoTrack,
    unpublishedScreenShareTrack,
    createLocalAudioTrack,
    createLocalVideoTrack,
    setUnpublishedAudioTrack,
    setUnpublishedVideoTrack,
  } = fromParticipantManager

  const { navToVideoView, navToChatView, setMemberCacheToChatView } =
    useCallNavHandler(activeMemberId, activeTopic)
  const { callErrors, addCallError } = useCallErrors()
  const {
    playOutgoingCallSound,
    playJoinedCallSound,
    playLeftCallSound,
    stopAllLoops,
  } = useContext(AppAudioContext)
  const { invalidateDevices } = useContext(DevicesContext)

  useEffect(() => {
    if (
      prevConnectionStatus !== CS.CALL_CONNECTED &&
      connectionStatus === CS.CALL_CONNECTED
    ) {
      playOutgoingCallSound()
    }
    if (
      prevConnectionStatus === CS.CALL_CONNECTED &&
      connectionStatus !== CS.CALL_CONNECTED
    ) {
      stopAllLoops()
    }
  }, [
    connectionStatus,
    playOutgoingCallSound,
    stopAllLoops,
    prevConnectionStatus,
  ])

  const beforeJoinCall = useCallback(() => {
    // Request device permissions if possible
    const deviceApi = (window as any)?.webkit
    if (deviceApi?.messageHandlers?.cordova_iab) {
      deviceApi.messageHandlers.cordova_iab.postMessage(
        JSON.stringify({ action: 'requestPermissions' }),
      )
    }
    dispatchConnectionAction(CE.JOINING)
  }, [])

  useEffect(() => {
    if (navToVideoViewAfterJoin) {
      navToVideoView()
      setNavToVideoViewAfterJoin(false)
    }
  }, [navToVideoViewAfterJoin, navToVideoView])

  const afterJoinCall = useCallback(
    (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      promise: Promise<{ data?: any }>,
      memberId?: number,
      {
        upgradingToRoom,
        callType,
      }: { upgradingToRoom?: boolean; callType?: 'audio' | 'video' } = {
        upgradingToRoom: false,
        callType: 'audio',
      },
    ) => {
      // Enable a power save block in Electron
      if (isElectron()) {
        blockPowerSave()
      }

      const preventSleep = (window as any)?.webkit?.messageHandlers
        ?.preventSleep
      if (preventSleep) {
        preventSleep.postMessage(true)
      }

      const insomnia = (window as any)?.plugins?.insomnia
      if (insomnia) {
        insomnia?.keepAwake()
      }

      return promise.then(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        async (result: any) => {
          /* react does not batch state updates in some async yet, promises is one.
            it is better that these state variables co-exist in the same render */

            if (!upgradingToRoom) {
              subscribe()
            }
            const mId = memberId || currentMemberId
            const { access_token: accessToken, records } = result.data
            dispatch(setFetched('calls', records))
            const call: any = findRecordInApiResponse(result, 'calls')
            const topicId = call?.topic_id
            const topic: any = findRecordInApiResponse(
              result,
              'topics',
              topicId,
            )

            const audioTrack =
              unpublishedAudioTrack ||
              (await createLocalAudioTrack().catch(() => null))
            const videoTrack =
              unpublishedVideoTrack ||
              (callType === 'video' &&
                (await createLocalVideoTrack().catch(() => null)))
            const tracks = [
              ...(audioTrack ? [audioTrack] : []),
              ...(videoTrack ? [videoTrack] : []),
              ...(unpublishedScreenShareTrack
                ? [unpublishedScreenShareTrack]
                : []),
            ]
            /* Refreshes the devices, so we can read the labels if permissions have now been granted.
            This services browsers that do not support the Permissions API, specifically, querying for the microphone
            and camera permission state */
            invalidateDevices?.()

            setActiveMemberId(mId)
            setActiveCall(call)
            setActiveTopic(topic)
            setNavToVideoViewAfterJoin(callType === 'video')

            if (accessToken) {
              // eslint-disable-next-line
              // @ts-ignore
              if (window?.webkit?.messageHandlers?.joinCall) {
                const connectOptions = getConnectionOptions()
                // eslint-disable-next-line
                // @ts-ignore
                window?.webkit?.messageHandlers?.joinCall?.postMessage({
                  accessToken: accessToken,
                  roomSid: call.room_sid,
                  connectOptions,
                })
              } else {
                return joinRoom(
                  dispatch,
                  axios,
                  accessToken,
                  call,
                  mId,
                  tracks,
                ).then(
                  async ({ room }) => {
                    if (room.participants.size) {
                      playJoinedCallSound()
                    }
                    dispatchConnectionAction(CE.ROOM_CONNECTED)
                    setRoom(room)

                    return Promise.resolve()
                  },
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
                  (error: TwilioError) => {
                    setUnpublishedAudioTrack(audioTrack)
                    if (videoTrack) {
                      setUnpublishedVideoTrack(videoTrack)
                    }
                    dispatchConnectionAction(CE.JOIN_ROOM_ERROR)
                    ErrorBoundary.logErrorToMyService(error)
                    return Promise.reject()
                  },
                )
              }
            } else {
              setUnpublishedAudioTrack(audioTrack)
              if (videoTrack) {
                setUnpublishedVideoTrack(videoTrack)
              }
              dispatchConnectionAction(CE.CALL_CONNECTED)
              return Promise.resolve()
            }

        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (error: any) => {
          addCallError(error.error_message)
          dispatchConnectionAction(CE.DISCONNECTED)

          return Promise.reject()
        },
      )
    },
    [
      currentMemberId,
      dispatch,
      unpublishedAudioTrack,
      createLocalAudioTrack,
      unpublishedVideoTrack,
      createLocalVideoTrack,
      unpublishedScreenShareTrack,
      invalidateDevices,
      subscribe,
      axios,
      playJoinedCallSound,
      setRoom,
      setUnpublishedAudioTrack,
      setUnpublishedVideoTrack,
      addCallError,
    ],
  )

  const joinCall = useCallback(
    ({ topicId, upgradingToRoom, callType } = { upgradingToRoom: false }) => {
      const memberId = topicId ? currentMemberId : activeMemberId
      topicId = topicId || activeTopicId
      if (!memberId || !topicId) {
        return Promise.reject()
      }
      beforeJoinCall()
      const promise: Promise<any> = dispatch(
        axios(joinCallTopic(memberId, topicId, { upgradingToRoom, callType })),
      )
      return afterJoinCall(promise, memberId, { upgradingToRoom, callType })
    },
    [
      activeMemberId,
      activeTopicId,
      afterJoinCall,
      axios,
      beforeJoinCall,
      currentMemberId,
      dispatch,
    ],
  )

  // handles joining to the webrtc room, once it becomes available
  // (the room is only created once 2 or more people are on call)
  useEffect(() => {
    const { room_sid: roomSid } = activeCall || {}
    const fireJoinRoom =
      connectionStatus === CS.CALL_CONNECTED &&
      roomSid &&
      roomSid !== roomData.lastClosedRoomSid &&
      !room
    if (fireJoinRoom && joinCall) {
      // eslint-disable-next-line
      // @ts-ignore
      joinCall({ upgradingToRoom: true }).catch(() => {
        // do nothing
      })
    }
  }, [joinCall, roomData, activeCall, connectionStatus, room])

  // if call goes back to one person, we have the opportunity to restore from the JOIN_ROOM_ERROR state
  useEffect(() => {
    const { room_sid: roomSid } = activeCall || {}
    if (connectionStatus === CS.JOIN_ROOM_ERROR && !roomSid) {
      dispatchConnectionAction(CE.CALL_CONNECTED)
    }
  }, [activeCall, connectionStatus])

  const joinCallForNewDirectChat = useCallback(
    (directChatHandle, { callType }) => {
      beforeJoinCall()
      const promise: Promise<any> = dispatch(
        axios(
          joinCallNewDirectChat(currentMemberId, directChatHandle, {
            callType,
          }),
        ),
      )
      return afterJoinCall(promise, currentMemberId, { callType })
    },
    [currentMemberId, afterJoinCall, axios, beforeJoinCall, dispatch],
  )

  const disconnectFromRoom = useCallback(() => {
    publishedAudioTrack?.stop()
    publishedVideoTrack?.stop()
    publishedScreenShareTrack?.stop()
    room?.disconnect()
  }, [
    publishedAudioTrack,
    publishedScreenShareTrack,
    publishedVideoTrack,
    room,
  ])

  const onDisconnect = useCallback(
    ({ externalDisconnect, playLeaveSound = true }) => {
      if (externalDisconnect) {
        dispatchConnectionAction(CE.DISCONNECTED_EXTERNALLY)
        disconnectFromRoom()
      } else {
        dispatchConnectionAction(CE.DISCONNECTED)
      }
      unsubscribe()
      exitFullscreen()
      if (playLeaveSound) {
        playLeftCallSound()
      }

      if (appLocation.topicView === TOPIC_VIEW.VIDEO_VIEW) {
        if (liveChatApp) {
          setAppLocation({ topicView: TOPIC_VIEW.CHAT_VIEW })
        } else {
          goBack && goBack()
        }
      }
      if (
        activeMemberId &&
        activeMemberId !== currentMemberId &&
        appLocationCache[activeMemberId]?.topicView === TOPIC_VIEW.VIDEO_VIEW &&
        setMemberCacheToChatView
      ) {
        setMemberCacheToChatView()
      }

      // If we're in the electron app, unblock any active power save
      if (isElectron()) {
        unblockPowerSave()
      }

      const preventSleep = (window as any)?.webkit?.messageHandlers
        ?.preventSleep
      if (preventSleep) {
        preventSleep.postMessage(false)
      }

      const insomnia = (window as any)?.plugins?.insomnia
      if (insomnia) {
        insomnia?.allowSleepAgain()
      }
    },
    [
      activeMemberId,
      appLocation.topicView,
      appLocationCache,
      currentMemberId,
      disconnectFromRoom,
      exitFullscreen,
      goBack,
      liveChatApp,
      playLeftCallSound,
      setAppLocation,
      setMemberCacheToChatView,
      unsubscribe,
    ],
  )

  const leaveCall = useCallback(
    (event, options = {}) => {
      options = {
        playLeaveSound: true,
        attempts: 1,
        ...options,
      }
      const { playLeaveSound, attempts } = options
      if (!activeMemberId || !activeTopicId) return Promise.reject()

      dispatchConnectionAction(CE.LEAVING_CALL)
      disconnectFromRoom()
      setNavToVideoViewAfterJoin(false)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const d: any = dispatch(
        axios(leaveCallAction(activeMemberId, activeTopicId, attempts)),
      )
      return d.then(
        () => {
          onDisconnect({ playLeaveSound })
          setActiveCall(null)
          setActiveTopic(null)
          setActiveMemberId(null)

        },
        (e: any) => {
          if (room?.state === CONNECTED) {
            dispatchConnectionAction(CE.ROOM_CONNECTED)
          } else if (room?.state === RECONNECTING) {
            dispatchConnectionAction(CE.ROOM_RECONNECTING)
          } else {
            dispatchConnectionAction(CE.CALL_CONNECTED)
          }
          const timedOut =
            e?.response?.data?.error?.error_message ===
            'Timed out, please try again'
          if (timedOut && attempts < 3) {
            leaveCall(undefined, { attempts: attempts + 1 })
          }
        },
      )
    },
    [
      activeMemberId,
      activeTopicId,
      axios,
      disconnectFromRoom,
      dispatch,
      onDisconnect,
      room?.state,
    ],
  )

  const contextValue = useMemo(() => {
    const firstJoining =
      connectionStatus === CS.JOINING &&
      prevConnectionStatus === CS.DISCONNECTED

    return {
      activeCall: {
        callRecord: activeCall,
        room,
        ...roomData,
        isOnCall:
          !!activeCall && connectionStatus !== CS.DISCONNECTED && !firstJoining,
        navToVideoView,
        navToChatView,
        setMemberCacheToChatView,
        callErrors,
        connectionStatus,
        prevConnectionStatus,
      } as ActiveCallType,
      joinCall,
      joinCallForNewDirectChat,
      isJoining: connectionStatus === CS.JOINING,
      leaveCall,
      openCalls,
    } as UseCallManagerType
  }, [
    activeCall,
    room,
    roomData,
    connectionStatus,
    navToVideoView,
    navToChatView,
    setMemberCacheToChatView,
    callErrors,
    prevConnectionStatus,
    joinCall,
    joinCallForNewDirectChat,
    leaveCall,
    openCalls,
  ])

  const rootStoreToSubStoreContextValue = {
    setActiveMemberId,
    setActiveCall,
    setActiveTopic,
    setActiveParticipant,
    setOpenCalls,
    onDisconnect,
    connectionStatus,
  }

  return (
    <CallManagerContext.Provider value={contextValue}>
      <CallsRootStoreToSubStoreContext.Provider
        value={rootStoreToSubStoreContextValue}
      >
        <ActiveCallAlert
          activeParticipant={activeParticipant}
          connectionStatus={connectionStatus}
        />
        <MemoizedParticipantManager
          room={room}
          passBackFromParticipantManager={passBackFromParticipantManager}
        >
          {children}
        </MemoizedParticipantManager>
      </CallsRootStoreToSubStoreContext.Provider>
    </CallManagerContext.Provider>
  )
}
